import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOnboarding } from '../hooks/useOnboarding';
import logo from '../logo.png';

/* Navigation items */
const navItems = [
  { path: '/', icon: 'dashboard', text: 'Dashboard' },
  { path: '/places', icon: 'location_on', text: 'Places' },
  { path: '/festivals', icon: 'celebration', text: 'Festivals' },
  { path: '/journals', icon: 'auto_stories', text: 'Journals' },
  { path: '/feedback', icon: 'chat_bubble', text: 'Feedback' },
  { path: '/users', icon: 'group', text: 'Users' },
  { path: '/analytics', icon: 'trending_up', text: 'Analytics' },
];

const AdminLayout = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { restartTour } = useOnboarding();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    await logout();
  };

  const handleStartTour = () => {
    setMobileMenuOpen(false);
    restartTour();
  };

  // Track mouse for subtle parallax
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 10,
        y: (e.clientY / window.innerHeight - 0.5) * 10,
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Close menus when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuOpen && !e.target.closest('[data-user-menu]')) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [userMenuOpen]);

  return (
    <div className="flex h-screen overflow-hidden bg-[#0a0a0f]">
      
      {/* ===== Animated Background ===== */}
      <div className="fixed inset-0 pointer-events-none">
        {/* Base gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/10 via-[#0a0a0f] to-indigo-900/10" />
        
        {/* Animated mesh gradient */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            background: `
              radial-gradient(circle at ${50 + mousePosition.x}% ${50 + mousePosition.y}%, rgba(139, 92, 246, 0.15) 0%, transparent 50%),
              radial-gradient(circle at ${30 - mousePosition.x}% ${70 - mousePosition.y}%, rgba(99, 102, 241, 0.1) 0%, transparent 40%)
            `,
            transition: 'background 0.5s ease-out'
          }}
        />

        {/* Floating orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-600/5 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '8s' }} />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-indigo-600/5 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '12s', animationDelay: '4s' }} />

        {/* Grid pattern */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.02]" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <pattern id="adminGrid" width="4" height="4" patternUnits="userSpaceOnUse">
              <circle cx="0.5" cy="0.5" r="0.3" fill="white" />
            </pattern>
          </defs>
          <rect width="100" height="100" fill="url(#adminGrid)" />
        </svg>
      </div>

      {/* ===== SIDEBAR (Desktop) ===== */}
      <aside className="hidden lg:flex w-20 flex-col items-center py-6 bg-[#0a0a0f] border-r border-white/5 relative z-50">
        
        {/* Logo */}
        <Link to="/" className="mb-8 group relative">
          <div className="absolute inset-0 bg-purple-500 rounded-xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity" />
          <div className="relative w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-indigo-600 p-0.5">
            <div className="w-full h-full rounded-xl bg-[#0a0a0f] flex items-center justify-center overflow-hidden">
              <img src={logo} alt="Know India" className="w-8 h-8 object-contain" />
            </div>
          </div>
        </Link>
        
        {/* Navigation */}
        <nav className="flex flex-col gap-2 flex-1">
          {navItems.map((item) => {
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                title={item.text}
                className={`
                  relative flex items-center justify-center w-12 h-12 rounded-xl
                  transition-all duration-300
                  ${active 
                    ? 'bg-purple-500/20 text-purple-400' 
                    : 'text-white/40 hover:text-white/80 hover:bg-white/5'
                  }
                `}
              >
                <span className="material-symbols-outlined text-[22px]">{item.icon}</span>
                
                {/* Active indicator */}
                {active && (
                  <div className="absolute -right-[1px] w-0.5 h-6 bg-purple-500 rounded-full" />
                )}
              </Link>
            );
          })}
        </nav>
        
        {/* Bottom Actions */}
        <div className="flex flex-col gap-2">
          {/* Tour */}
          <button
            onClick={handleStartTour}
            title="Take a Tour"
            className="relative flex items-center justify-center w-12 h-12 rounded-xl text-white/40 hover:text-white/80 hover:bg-white/5 transition-all duration-300"
          >
            <span className="material-symbols-outlined text-[22px]">route</span>
          </button>
          
          {/* User Avatar */}
          <div className="relative" data-user-menu>
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden hover:border-purple-500/50 transition-all duration-300"
            >
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt={user.name || 'Admin'} className="w-full h-full object-cover" />
              ) : (
                <span className="text-sm font-medium text-white/60">
                  {user?.name?.charAt(0) || 'A'}
                </span>
              )}
            </button>
            
            {/* User Dropdown */}
            {userMenuOpen && (
              <div className="absolute left-full bottom-0 ml-4 w-64 bg-[#1a1a2e] backdrop-blur-xl rounded-2xl border border-white/10 py-2 animate-fade-in z-[100] shadow-xl">
                <div className="px-4 py-3 border-b border-white/5">
                  <p className="text-sm font-medium text-white">{user?.name || 'Admin'}</p>
                  <p className="text-xs text-white/40 truncate">{user?.email || 'admin@knowindia.com'}</p>
                </div>
                <div className="py-2">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                  >
                    <span className="material-symbols-outlined text-lg">logout</span>
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* ===== MAIN CONTENT AREA ===== */}
      <main className="flex-1 overflow-y-auto relative z-10">
        
        {/* ===== HEADER ===== */}
        <header className="sticky top-0 z-40 px-6 lg:px-8 py-4 bg-[#0a0a0f]/80 backdrop-blur-xl border-b border-white/5">
          <div className="flex items-center justify-between">
            
            {/* Left: Branding */}
            <Link to="/" className="flex items-center gap-3 group">
              <div className="lg:hidden relative">
                <div className="absolute inset-0 bg-purple-500 rounded-lg blur opacity-30" />
                <div className="relative w-10 h-10 rounded-lg bg-gradient-to-br from-purple-600 to-indigo-600 p-0.5">
                  <div className="w-full h-full rounded-lg bg-[#0a0a0f] flex items-center justify-center overflow-hidden">
                    <img src={logo} alt="Know India" className="w-6 h-6 object-contain" />
                  </div>
                </div>
              </div>
              <div>
                <h1 className="text-xl lg:text-2xl font-extralight tracking-tight text-white">
                  Know<span className="font-bold bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent">India</span>
                </h1>
                <p className="text-[10px] text-white/30 tracking-[0.2em] uppercase hidden sm:block">
                  Admin Portal
                </p>
              </div>
            </Link>
            
            {/* Center: Search (Desktop) */}
            <div className="hidden md:block flex-1 max-w-md mx-8">
              <div className="relative group">
                <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-lg group-focus-within:text-purple-400 transition-colors">
                  search
                </span>
                <input
                  type="text"
                  placeholder="Search..."
                  className="w-full pl-12 pr-4 py-3 bg-white/[0.03] border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-purple-500/50 focus:bg-white/[0.05] transition-all"
                />
              </div>
            </div>
            
            {/* Right: Actions */}
            <div className="flex items-center gap-3">
              {/* Add Place Button (Desktop) */}
              <Link
                to="/places/new"
                className="hidden md:flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white text-sm font-medium rounded-xl transition-all duration-300 shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40"
              >
                <span className="material-symbols-outlined text-sm">add</span>
                Add Place
              </Link>
              
              {/* Notifications */}
              <button className="hidden lg:flex items-center justify-center w-10 h-10 rounded-xl bg-white/[0.03] border border-white/10 text-white/40 hover:text-white/80 hover:border-white/20 transition-all">
                <span className="material-symbols-outlined text-xl">notifications</span>
              </button>
              
              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden flex items-center justify-center w-10 h-10 rounded-xl bg-white/[0.03] border border-white/10 text-white/60 hover:text-white transition-all"
              >
                <span className="material-symbols-outlined">
                  {mobileMenuOpen ? 'close' : 'menu'}
                </span>
              </button>
            </div>
          </div>
        </header>

        {/* ===== MOBILE NAVIGATION DRAWER ===== */}
        {mobileMenuOpen && (
          <>
            <div 
              className="lg:hidden fixed inset-0 bg-black/80 backdrop-blur-sm z-40"
              onClick={() => setMobileMenuOpen(false)}
            />
            <div className="lg:hidden fixed top-[73px] left-0 right-0 bg-[#0a0a0f]/95 backdrop-blur-xl border-b border-white/10 z-40 animate-fade-in-down max-h-[calc(100vh-73px)] overflow-y-auto">
              <nav className="p-4 space-y-1">
                {navItems.map((item) => {
                  const active = isActive(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                        active 
                          ? 'bg-purple-500/20 text-purple-400' 
                          : 'text-white/60 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <span className="material-symbols-outlined text-xl">{item.icon}</span>
                      <span className="font-medium">{item.text}</span>
                    </Link>
                  );
                })}
                
                <div className="h-px bg-white/10 my-4" />
                
                <Link
                  to="/places/new"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-purple-400 hover:bg-purple-500/10 transition-all"
                >
                  <span className="material-symbols-outlined text-xl">add_circle</span>
                  <span className="font-medium">Add New Place</span>
                </Link>
                
                <button
                  onClick={handleStartTour}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-white/60 hover:text-white hover:bg-white/5 transition-all"
                >
                  <span className="material-symbols-outlined text-xl">route</span>
                  <span className="font-medium">Take a Tour</span>
                </button>

                <div className="h-px bg-white/10 my-4" />
                
                <div className="flex items-center gap-3 px-4 py-3">
                  {user?.avatar_url ? (
                    <img src={user.avatar_url} alt={user.name || 'Admin'} className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-white font-medium">
                      {user?.name?.charAt(0) || 'A'}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-white truncate">{user?.name || 'Admin'}</p>
                    <p className="text-xs text-white/40 truncate">{user?.email || 'admin@knowindia.com'}</p>
                  </div>
                </div>
                
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-400 hover:bg-red-500/10 transition-all"
                >
                  <span className="material-symbols-outlined text-xl">logout</span>
                  <span className="font-medium">Sign Out</span>
                </button>
              </nav>
            </div>
          </>
        )}

        {/* ===== PAGE CONTENT ===== */}
        <div className="px-6 lg:px-8 py-6 animate-fade-in">
            <Outlet />
          </div>

        {/* ===== FOOTER ===== */}
        <footer className="px-6 lg:px-8 py-4 border-t border-white/5 mt-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/30">
            <span>© 2026 Know India. All rights reserved.</span>
            <a 
              href="https://knowindia.aryankr.in" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-purple-400 hover:text-purple-300 transition-colors flex items-center gap-1"
            >
              Visit Website
              <span className="material-symbols-outlined text-sm">arrow_outward</span>
            </a>
          </div>
        </footer>
        </main>
    </div>
  );
};

export default AdminLayout;
